import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query"

import { invalidationIntents, queryKeys } from "~/api/cache"

import type { UpdateProposalConsumptionResponseDto } from "../dto/proposal-consumption.dto"
import { proposalConsumptionService } from "../services/proposal-consumption.service"

export type SwitchProposalConsumptionSelectionVariables = {
  proposalId: string
  /** Site whose Green Button data should refresh after the switch. */
  siteId?: string
  profileId: string
  confirmedDegradedUsage?: boolean
  reason?: string
  requestId: string
}

export default function useSwitchProposalConsumptionSelection(): UseMutationResult<
  UpdateProposalConsumptionResponseDto,
  unknown,
  SwitchProposalConsumptionSelectionVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      proposalId,
      profileId,
      confirmedDegradedUsage,
      reason,
      requestId,
    }: SwitchProposalConsumptionSelectionVariables) =>
      proposalConsumptionService.switchSelection(
        proposalId,
        {
          profileId,
          reason: reason ?? null,
          confirmedDegradedUsage: confirmedDegradedUsage ?? false,
        },
        requestId,
      ),
    onSuccess: (data, { proposalId, siteId }) => {
      queryClient.setQueryData(
        queryKeys.proposalConsumption.selection(proposalId),
        data,
      )
      void queryClient.invalidateQueries({
        queryKey: queryKeys.proposalConsumption.insights(proposalId),
      })
      if (siteId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.arcadiaGreenButton.site(siteId),
        })
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.byId(proposalId),
      })
      void invalidationIntents.proposalPricingInputsChanged(
        queryClient,
        proposalId,
      )
    },
  })
}
